import type { EventTypeCode } from "../types/events";
import { EVENT_TYPE_UI, type EventTypeUiEntry } from "./events.config";
import { formatEventFieldValue } from "./format-event-field-value";
import { pickAttributeValue } from "./pick-event-attribute";

export type EventDetailDisplayRow = {
	label: string;
	value: string;
	tooltip?: string;
};
const WEEKDAY_LABELS_PT: Record<number, string> = {
	0: "Domingo",
	1: "Segunda-feira",
	2: "Terça-feira",
	3: "Quarta-feira",
	4: "Quinta-feira",
	5: "Sexta-feira",
	6: "Sábado",
};

function normalizeEventDisplayValue(value: unknown): string {
	if (value === undefined || value === null) {
		return "";
	}
	const text = String(value).trim();
	if (!text || text === "-" || text === " - ") {
		return "";
	}
	return text;
}

function toEventTypeCode(raw: unknown): EventTypeCode | null {
	const n = Number(raw);
	if (!(Number.isFinite(n) && Number.isInteger(n)) || n < 1 || n > 13) {
		return null;
	}
	return n as EventTypeCode;
}

function formatPtDecimal(value: number, maximumFractionDigits = 2): string {
	return value.toLocaleString("pt-BR", {
		minimumFractionDigits: 0,
		maximumFractionDigits,
	});
}

export function buildEventTechnicalDisplayRows(
	eventType: unknown,
	attributes: Record<string, number | string>
): EventDetailDisplayRow[] {
	const eventTypeCode = toEventTypeCode(eventType);
	if (!eventTypeCode) {
		return [];
	}

	const eventTypeDefinition: EventTypeUiEntry = EVENT_TYPE_UI[eventTypeCode];
	const technicalRows: EventDetailDisplayRow[] = [];

	for (const technicalField of eventTypeDefinition.technicalFields) {
		if (eventTypeCode === 10 && technicalField.id === "time") {
			const dayRaw = pickAttributeValue(attributes, ["day", "Day"]);
			const timeRaw = pickAttributeValue(attributes, ["time", "Time"]);
			const dayNumber = Number(dayRaw);
			const dayLabelRaw =
				dayRaw !== undefined
					? Number.isInteger(dayNumber)
						? (WEEKDAY_LABELS_PT[dayNumber] ?? String(dayRaw))
						: String(dayRaw)
					: "";
			const dayLabel = normalizeEventDisplayValue(dayLabelRaw);
			const timeLabel = normalizeEventDisplayValue(timeRaw);

			const scheduleLabel = [dayLabel, timeLabel]
				.filter((part) => part && part.trim() !== "")
				.join("\n");

			if (scheduleLabel) {
				technicalRows.push({
					label: technicalField.label,
					value: scheduleLabel,
					tooltip: technicalField.description,
				});
				continue;
			}
		}

		const rawAttributeValue = pickAttributeValue(
			attributes,
			technicalField.sourceKeys
		);
		if (rawAttributeValue === undefined) {
			continue;
		}
		const normalizedAttributeValue =
			normalizeEventDisplayValue(rawAttributeValue);
		if (normalizedAttributeValue === "") {
			continue;
		}

		if (eventTypeCode === 2 && technicalField.id === "speed") {
			const formattedSpeed = formatEventFieldValue(
				technicalField.format,
				rawAttributeValue,
				eventTypeCode
			);
			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);
			const formattedLimit =
				normalizedLimit === ""
					? ""
					: formatEventFieldValue("limit_kmh", normalizedLimit, eventTypeCode);

			technicalRows.push({
				label: technicalField.label,
				value: formattedLimit
					? `${formattedSpeed} (> ${formattedLimit})`
					: formattedSpeed,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 1 && technicalField.id === "seconds") {
			const formattedSeconds = formatEventFieldValue(
				technicalField.format,
				rawAttributeValue,
				eventTypeCode
			);
			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);
			const formattedLimit =
				normalizedLimit === ""
					? ""
					: formatEventFieldValue("seconds", normalizedLimit, eventTypeCode);

			technicalRows.push({
				label: technicalField.label,
				value: formattedLimit
					? `${formattedSeconds} (> ${formattedLimit})`
					: formattedSeconds,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 3 && technicalField.id === "speed_final") {
			const rawAcceleration = pickAttributeValue(attributes, [
				"acceleration",
				"Acceleration",
				"speed_final",
				"speedFinal",
			]);
			const normalizedAcceleration =
				normalizeEventDisplayValue(rawAcceleration);
			if (normalizedAcceleration === "") {
				continue;
			}

			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);

			technicalRows.push({
				label: technicalField.label,
				value: normalizedLimit
					? `${normalizedAcceleration} Km/h/s (> ${normalizedLimit} Km/h/s)`
					: `${normalizedAcceleration} Km/h/s`,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 4 && technicalField.id === "g_force") {
			const rawForce = pickAttributeValue(attributes, ["g_force", "gForce"]);
			const normalizedForce = normalizeEventDisplayValue(rawForce);
			if (normalizedForce === "") {
				continue;
			}

			const rawSpeed = pickAttributeValue(attributes, ["speed"]);
			const normalizedSpeed = normalizeEventDisplayValue(rawSpeed);
			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);

			const forceNumber = Number(normalizedForce);
			const speedNumber = Number(normalizedSpeed);
			const limitNumber = Number(normalizedLimit);

			const formattedForce = Number.isFinite(forceNumber)
				? `${formatPtDecimal(forceNumber)} G`
				: normalizedForce;
			const formattedSpeed =
				Number.isFinite(speedNumber) && speedNumber > 0
					? `${Math.round(speedNumber)} km/h`
					: "";
			const formattedLimit = Number.isFinite(limitNumber)
				? `${formatPtDecimal(limitNumber)} G`
				: normalizedLimit;

			technicalRows.push({
				label: technicalField.label,
				value: formattedLimit
					? `${formattedForce}${formattedSpeed ? ` a ${formattedSpeed}` : ""} (> ${formattedLimit})`
					: `${formattedForce}${formattedSpeed ? ` a ${formattedSpeed}` : ""}`,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 5 && technicalField.id === "speed_final") {
			const rawDeceleration = pickAttributeValue(attributes, [
				"speed_ini",
				"speedIni",
				"deceleration",
				"Deceleration",
				"speed_final",
				"speedFinal",
			]);
			const normalizedDeceleration =
				normalizeEventDisplayValue(rawDeceleration);
			if (normalizedDeceleration === "") {
				continue;
			}

			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);

			technicalRows.push({
				label: technicalField.label,
				value: normalizedLimit
					? `${normalizedDeceleration} Km/h/s (> ${normalizedLimit} Km/h/s)`
					: `${normalizedDeceleration} Km/h/s`,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 13 && technicalField.id === "current_km") {
			const formattedCurrentKm = formatEventFieldValue(
				technicalField.format,
				rawAttributeValue,
				eventTypeCode
			).replace(/\s?km$/i, "");
			const rawGoalKm = pickAttributeValue(attributes, ["goal_km", "goalKm"]);
			const normalizedGoalKm = normalizeEventDisplayValue(rawGoalKm);
			const formattedGoalKm =
				normalizedGoalKm === ""
					? ""
					: formatEventFieldValue(
							"km",
							normalizedGoalKm,
							eventTypeCode
						).replace(/\s?km$/i, "");

			technicalRows.push({
				label: technicalField.label,
				value: formattedGoalKm
					? `${formattedCurrentKm} / ${formattedGoalKm} Km`
					: `${formattedCurrentKm} Km`,
				tooltip: technicalField.description,
			});
			continue;
		}

		if (eventTypeCode === 12 && technicalField.id === "voltage") {
			const voltageNumber = Number(normalizedAttributeValue);
			const formattedVoltage = Number.isFinite(voltageNumber)
				? `${formatPtDecimal(voltageNumber)} V`
				: `${normalizedAttributeValue} V`;

			const rawLimit = pickAttributeValue(attributes, ["limit", "Limit"]);
			const normalizedLimit = normalizeEventDisplayValue(rawLimit);
			const limitNumber = Number(normalizedLimit);
			const formattedLimit = Number.isFinite(limitNumber)
				? `${formatPtDecimal(limitNumber)} V`
				: normalizedLimit
					? `${normalizedLimit} V`
					: "";

			technicalRows.push({
				label: technicalField.label,
				value: formattedLimit
					? `${formattedVoltage} (Abaixo do Limite de ${formattedLimit})`
					: formattedVoltage,
				tooltip: technicalField.description,
			});
			continue;
		}

		technicalRows.push({
			label: technicalField.label,
			value: formatEventFieldValue(
				technicalField.format,
				rawAttributeValue,
				eventTypeCode
			),
			tooltip: technicalField.description,
		});
	}

	if (
		eventTypeCode === 9 &&
		technicalRows.length === 0 &&
		eventTypeDefinition.emptyTechnicalFallback
	) {
		technicalRows.push({
			label: eventTypeDefinition.emptyTechnicalFallback.label,
			value: eventTypeDefinition.emptyTechnicalFallback.value,
			tooltip: eventTypeDefinition.emptyTechnicalFallback.description,
		});
	}

	return technicalRows;
}
